const GRADE_REPEAT_COUNTS = { 0: 16, 1: 16, 2: 12, 3: 8, 4: 8, 5: 4 };
const SIMPLE_GRADE_REPEAT_COUNTS = { 0: 8, 1: 8, 2: 8, 3: 4, 4: 4, 5: 4 };
const READING_GRADE_REPEAT_COUNTS = { 0: 8, 1: 8, 2: 8, 3: 4, 4: 2, 5: 2 };
const DIALOG_GRADE_REPEAT_COUNTS = READING_GRADE_REPEAT_COUNTS;
const SIMPLE_CARD_TYPE = 'simple';
const READING_CARD_TYPE = 'reading';
const DIALOG_CARD_TYPE = 'dialog';
const NEVER_PRACTICED_PRIORITY = 1_000_000_000_000_000;
const DUE_ASSESSMENT_PRIORITY = 2_000_000_000_000_000;
const LAST_PRACTICED_DAY_PRIORITY = 1_000_000_000;
const CREATED_DAY_PRIORITY = 1;
const PRACTICE_SHUFFLE_BLOCK_SIZE = 10;

function getCardId(card) {
  return card?.id ?? card?._id;
}

function normalizeGrade(value) {
  const grade = Number(value);
  return Number.isFinite(grade) && grade >= 1 && grade <= 5 ? grade : 0;
}

function getLastGrade(card) {
  if (!card) return 0;
  return normalizeGrade(card.assessmentGrade);
}

function getAssessmentGrade(card) {
  if (card?.cardType === READING_CARD_TYPE || card?.cardType === DIALOG_CARD_TYPE) return 0;

  return normalizeGrade(card?.assessmentGrade);
}

function getDaysSince(card, now = new Date()) {
  if (!card) return null;
  const lastTimestamp = card.lastPracticedAt ?? null;
  if (!lastTimestamp) return null;
  const last = new Date(lastTimestamp);
  const current = new Date(now);
  if (Number.isNaN(last.getTime()) || Number.isNaN(current.getTime())) return null;
  last.setHours(0, 0, 0, 0);
  current.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((current - last) / 86400000));
}

function getAgeScore(daysSince) {
  if (daysSince === null) return NEVER_PRACTICED_PRIORITY;
  return Number.isFinite(daysSince) ? Math.max(0, daysSince) : 0;
}

function getAgePriorityMultiplier(daysSince) {
  return getAgeScore(daysSince);
}

function getRepeatCountForGrade(grade, card = null) {
  if (card?.cardType === DIALOG_CARD_TYPE) {
    return READING_GRADE_REPEAT_COUNTS[grade] ?? READING_GRADE_REPEAT_COUNTS[1];
  }
  if (card?.cardType === READING_CARD_TYPE) {
    return READING_GRADE_REPEAT_COUNTS[grade] ?? READING_GRADE_REPEAT_COUNTS[1];
  }
  if (card?.cardType === SIMPLE_CARD_TYPE) {
    return SIMPLE_GRADE_REPEAT_COUNTS[grade] ?? SIMPLE_GRADE_REPEAT_COUNTS[1];
  }
  return GRADE_REPEAT_COUNTS[grade] ?? 11;
}

function getCreatedDays(card, now = new Date()) {
  const createdAt = card?.createdAt ?? card?.created_at ?? card?.noteCreatedAt;
  if (!createdAt) return 0;

  const created = new Date(createdAt);
  const current = new Date(now);
  if (Number.isNaN(created.getTime()) || Number.isNaN(current.getTime())) return 0;

  created.setHours(0, 0, 0, 0);
  current.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((current - created) / 86400000));
}

function scoreCard(card, _legacyWeights = null, now = new Date()) {
  void _legacyWeights;
  const daysSinceLastPractice = getDaysSince(card, now);
  const lastPracticePriority = daysSinceLastPractice === null
    ? NEVER_PRACTICED_PRIORITY
    : daysSinceLastPractice * LAST_PRACTICED_DAY_PRIORITY;
  const createdPriority = getCreatedDays(card, now) * CREATED_DAY_PRIORITY;
  const assessmentPriority = getAssessmentGrade(card) === 4 ? DUE_ASSESSMENT_PRIORITY : 0;

  return assessmentPriority + lastPracticePriority + createdPriority;
}

function selectNextCard(cardPool, recentCardIds, weights) {
  if (!cardPool || cardPool.length === 0) return null;

  const recentIds = (recentCardIds || []).filter(Boolean);
  const recentSet = new Set(recentIds);
  const mostRecentId = recentIds[recentIds.length - 1];

  const scored = cardPool.map((card) => {
    return { card, score: scoreCard(card, weights) };
  });

  let candidates = scored.filter((item) => {
    const cardId = getCardId(item.card);
    return !(cardId && recentSet.has(cardId));
  });

  if (candidates.length === 0 && mostRecentId && cardPool.length > 1) {
    candidates = scored.filter((item) => getCardId(item.card) !== mostRecentId);
  }

  if (candidates.length === 0) {
    candidates = scored;
  }

  const bestScore = Math.max(...candidates.map((s) => Math.max(0, s.score)));
  if (bestScore === 0) {
    return candidates[Math.floor(Math.random() * candidates.length)].card;
  }

  const bestCards = candidates.filter((s) => Math.max(0, s.score) === bestScore);
  return bestCards[Math.floor(Math.random() * bestCards.length)].card;
}

function isReadingPractice(card) {
  return Boolean(card?.isReadingGroup)
    || card?.cardType === READING_CARD_TYPE
    || card?.cardType === DIALOG_CARD_TYPE;
}

function getNoteId(card) {
  const noteId = card?.noteId;
  return noteId === undefined || noteId === null || noteId === '' ? null : String(noteId);
}

function shufflePriorityBlocks(cards) {
  const shuffled = [...cards];
  for (let start = 0; start < shuffled.length; start += PRACTICE_SHUFFLE_BLOCK_SIZE) {
    const end = Math.min(start + PRACTICE_SHUFFLE_BLOCK_SIZE, shuffled.length);
    // Fisher-Yates within each priority block, including the final partial block.
    for (let index = end - 1; index > start; index -= 1) {
      const swapIndex = start + Math.floor(Math.random() * (index - start + 1));
      [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
    }
  }
  return shuffled;
}

function diversifyPracticeQueue(cards) {
  const pending = [...cards];
  const queue = [];

  while (pending.length > 0) {
    const last = queue[queue.length - 1];
    const lastNoteId = getNoteId(last);
    const blockedNoteId = lastNoteId !== null && lastNoteId === getNoteId(queue[queue.length - 2])
      ? lastNoteId
      : null;
    const avoidReading = isReadingPractice(last);
    const allowsNote = (card) => blockedNoteId === null || getNoteId(card) !== blockedNoteId;
    const allowsType = (card) => !avoidReading || !isReadingPractice(card);

    // Search beyond the current block when one newly added Note occupies it all.
    let nextIndex = pending.findIndex((card) => allowsNote(card) && allowsType(card));
    // When both rules cannot be met, prefer a break from Reading, then Note variety.
    if (nextIndex < 0 && avoidReading) nextIndex = pending.findIndex(allowsType);
    if (nextIndex < 0 && blockedNoteId !== null) nextIndex = pending.findIndex(allowsNote);
    if (nextIndex < 0) nextIndex = 0;

    // Pull forward only the replacement; the deferred head remains first pending.
    queue.push(pending.splice(nextIndex, 1)[0]);
  }

  return queue;
}

function createPracticeQueue(cardPool, _legacyWeights = null, now = new Date(), { randomOrder = false } = {}) {
  void _legacyWeights;
  if (!Array.isArray(cardPool) || cardPool.length === 0) return [];

  const seenIds = new Set();
  const rankedCards = cardPool
    .filter((card) => {
      const cardId = getCardId(card);
      if (!cardId) return true;
      if (seenIds.has(cardId)) return false;
      seenIds.add(cardId);
      return true;
    })
    .map((card, index) => ({
      card,
      score: randomOrder ? 0 : scoreCard(card, null, now),
      tieBreaker: Math.random(),
      index,
    }))
    .sort((a, b) => {
      const scoreDiff = Math.max(0, b.score) - Math.max(0, a.score);
      if (scoreDiff !== 0) return scoreDiff;

      const tieDiff = b.tieBreaker - a.tieBreaker;
      if (tieDiff !== 0) return tieDiff;

      return a.index - b.index;
    });

  if (randomOrder) return rankedCards.map((item) => item.card);

  return diversifyPracticeQueue(shufflePriorityBlocks(rankedCards.map((item) => item.card)));
}

const AutonomousSchedulerService = {
  selectNextCard,
  createPracticeQueue,
  scoreCard,
  getCardId,
  getLastGrade,
  getAssessmentGrade,
  getAgeScore,
  getAgePriorityMultiplier,
  getCreatedDays,
  getRepeatCountForGrade,
  GRADE_REPEAT_COUNTS,
  SIMPLE_GRADE_REPEAT_COUNTS,
  READING_GRADE_REPEAT_COUNTS,
  DIALOG_GRADE_REPEAT_COUNTS,
};

export default AutonomousSchedulerService;
